import React, { useEffect, useState, useRef } from 'react';
import {
  getFactories, createFactory, updateFactory, deleteFactory, reorderFactories, getShops,
} from '../../services/api';
import toast from 'react-hot-toast';

const dragHandleStyle = { cursor: 'grab', fontSize: 16, userSelect: 'none' };

export default function Factories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  // Which shops may order from this factory. We hold the YESes here and send
  // the NOes, so everything ticked saves no rows at all and a shop created
  // later sees the factory automatically.
  const [shops, setShops] = useState([]);
  const [shopIds, setShopIds] = useState([]);

  const [draggingId, setDraggingId] = useState(null);
  const dragItem = useRef(null);
  const dragBackup = useRef(null);
  const lastTarget = useRef(null);
  const dropDone = useRef(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([getFactories(showInactive), getShops(false)])
      .then(([fRes, sRes]) => { setItems(fRes.data); setShops(sRes.data); })
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null); setName('');
    setShopIds(shops.map((s) => s.id));   // visible everywhere by default
    setShowModal(true);
  };
  const openEdit = (f) => {
    setEditing(f); setName(f.name);
    const hidden = f.hidden_shop_ids || [];
    setShopIds(shops.filter((s) => !hidden.includes(s.id)).map((s) => s.id));
    setShowModal(true);
  };
  const toggleShop = (id) => {
    setShopIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const save = async () => {
    if (!name.trim()) return toast.error('Συμπληρώστε όνομα');
    if (shops.length > 0 && shopIds.length === 0) {
      return toast.error('Επιλέξτε τουλάχιστον ένα κατάστημα');
    }
    setSaving(true);
    try {
      const hidden_shop_ids = shops.filter((s) => !shopIds.includes(s.id)).map((s) => s.id);
      if (editing) { await updateFactory(editing.id, { name, hidden_shop_ids }); toast.success('Ενημερώθηκε'); }
      else { await createFactory({ name, hidden_shop_ids }); toast.success('Δημιουργήθηκε'); }
      setShowModal(false);
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSaving(false); }
  };

  const softDelete = async (f) => {
    if (!window.confirm(`Απενεργοποίηση: ${f.name};`)) return;
    try { await deleteFactory(f.id, false); toast.success('Απενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const reactivate = async (f) => {
    try { await updateFactory(f.id, { is_active: true }); toast.success('Ενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const hardDelete = async (f) => {
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ: ${f.name};\nΑυτή η ενέργεια δεν αναιρείται.`)) return;
    try { await deleteFactory(f.id, true); toast.success('Διαγράφηκε οριστικά'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  const onDragStart = (e, f) => {
    dragItem.current = f; dragBackup.current = null;
    lastTarget.current = null; dropDone.current = false;
    setDraggingId(f.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', '');
  };
  const onDragOver = (e, target) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.id === target.id) return;
    if (lastTarget.current === target.id) return;
    setItems((prev) => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const fi = prev.findIndex((x) => x.id === dragItem.current.id);
      const ti = prev.findIndex((x) => x.id === target.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...prev]; n.splice(fi, 1); n.splice(ti, 0, dragItem.current);
      lastTarget.current = target.id;
      return n;
    });
  };
  const onDrop = (e) => { e.preventDefault(); dropDone.current = true; };
  const onDragEnd = async () => {
    const wasDrop = dropDone.current; const backup = dragBackup.current;
    setDraggingId(null); dragItem.current = null;
    lastTarget.current = null; dropDone.current = false;
    if (!wasDrop) { if (backup) setItems(backup); dragBackup.current = null; return; }
    dragBackup.current = null;
    try { await reorderFactories(items.map((x) => x.id)); }
    catch { toast.error('Σφάλμα σειράς'); if (backup) setItems(backup); }
  };

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div><h2>🏭 Εργοστάσια</h2><p>Διαχείριση εργοστασίων</p></div>
          <div className="flex-row">
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-primary" onClick={openCreate}>+ Νέο Εργοστάσιο</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="page-loading"><span className="loading-spinner dark" /></div>
            ) : items.length === 0 ? (
              <div className="empty-state"><div>🏭</div><p>Δεν υπάρχουν εργοστάσια</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map((f) => {
                  const isDragging = draggingId === f.id;
                  return (
                    <div key={f.id}
                      onDragOver={(e) => onDragOver(e, f)} onDrop={onDrop}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        border: '1px solid var(--gray-100)', borderRadius: 8,
                        background: isDragging ? '#dcfce7' : (f.is_active ? 'white' : 'var(--gray-50)'),
                        opacity: isDragging ? 0.5 : (f.is_active ? 1 : 0.6),
                      }}>
                      <span draggable onDragStart={(e) => onDragStart(e, f)} onDragEnd={onDragEnd}
                        style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}>⠿</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, color: 'var(--gray-800)' }}>
                          {f.name}
                          {!f.is_active && <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>(ανενεργό)</span>}
                        </div>
                        {(f.hidden_shop_ids || []).length > 0 && (
                          <div style={{ fontSize: 11, color: '#b45309' }}>
                            🏪 όχι σε {(f.hidden_shop_ids || [])
                              .map((id) => (shops.find((s) => s.id === id) || {}).name)
                              .filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex-row">
                        {f.is_active ? (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(f)}>✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={() => softDelete(f)}>🗑</button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => reactivate(f)} title="Επαναφορά">↻</button>
                            <button className="btn btn-danger btn-sm" onClick={() => hardDelete(f)} title="Οριστική Διαγραφή">❌</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 460, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>{editing ? 'Επεξεργασία Εργοστασίου' : 'Νέο Εργοστάσιο'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Όνομα *</label>
                <input className="form-input" value={name} onChange={(e) => setName(e.target.value)}
                  autoFocus onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Διαθέσιμο στα καταστήματα</label>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                  Όλα επιλεγμένα = το εργοστάσιο φαίνεται παντού. Ξετσεκάρετε ένα κατάστημα
                  και το εργοστάσιο εξαφανίζεται από τη Νέα Παραγγελία εκεί, μαζί με όσα
                  προϊόντα δεν φτιάχνονται πουθενά αλλού.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                  {shops.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν καταστήματα.</div>
                  ) : (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)', paddingBottom: 6 }}>
                        <input type="checkbox" checked={shopIds.length === shops.length}
                          onChange={(e) => setShopIds(e.target.checked ? shops.map((s) => s.id) : [])} />
                        Όλα τα καταστήματα
                      </label>
                      {shops.map((s) => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                          <input type="checkbox" checked={shopIds.includes(s.id)} onChange={() => toggleShop(s.id)} />
                          {s.name}
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
